import { DOCUMENT } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter } from 'rxjs';

import { environment } from '../../environments/environment';
import { GalleryService } from '../core/gallery.service';

const PHONE_ICON =
  'M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z';

/**
 * Whether a computed background colour, e.g. "rgb(8, 15, 31)", is dark. Only the
 * rgb()/rgba() form browsers report for solid colours is understood; anything else
 * (transparent, or a colour-mix from an opacity modifier) counts as not dark.
 */
export function isDarkColour(colour: string): boolean {
  if (!/^rgba?\(/.test(colour)) {
    return false;
  }

  const [r, g, b, a = 1] = (colour.match(/[\d.]+/g) ?? []).map(Number);

  if (r === undefined || g === undefined || b === undefined || a < 0.5) {
    return false;
  }

  // Relative luminance, roughly: dark banners sit well under 0.25.
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255 < 0.25;
}

/**
 * Site header. It takes on the colour of the page's dark top banner (the home page
 * hero, or the navy title band on inner pages) while that banner is behind it, and
 * turns white once the banner has scrolled away and the white page shows.
 *
 * The banner is simply the page's first <section>. A page that opens on a light
 * section gets the white header throughout.
 */
@Component({
  selector: 'app-site-header',
  imports: [RouterLink, RouterLinkActive],
  template: `
    <a
      href="#main"
      class="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-lg focus:bg-navy-900 focus:px-4 focus:py-2 focus:text-white"
    >
      Skip to content
    </a>

    <header
      #header
      class="sticky top-0 z-40 border-b transition-colors duration-300"
      [class]="dark() ? 'border-white/10' : 'border-navy-100 bg-white/95 backdrop-blur'"
      [style.background-color]="dark() ? bannerColour() : null"
      [attr.data-theme]="dark() ? 'dark' : 'light'"
    >
      <div class="container-page flex h-18 items-center justify-between gap-4 py-3">
        <a routerLink="/" class="flex shrink-0 items-center" (click)="close()">
          <img
            src="images/fine-shine-logo.webp"
            alt="Fine Shine Car Wash"
            width="465"
            height="90"
            class="h-9 w-auto sm:h-10 lg:h-11"
          />
        </a>

        <nav class="hidden items-center gap-1 lg:flex" aria-label="Main">
          @for (link of links(); track link.path) {
            <a
              [routerLink]="link.path"
              [routerLinkActiveOptions]="{ exact: link.path === '/' }"
              routerLinkActive="is-active"
              ariaCurrentWhenActive="page"
              class="relative rounded-lg px-3.5 py-2 text-[0.9375rem] font-medium transition-colors after:absolute after:inset-x-3.5 after:-bottom-0.5 after:h-0.5 after:origin-left after:scale-x-0 after:rounded-full after:transition-transform [&.is-active]:after:scale-x-100"
              [class]="
                dark()
                  ? 'text-white/80 after:bg-brand-400 hover:text-white [&.is-active]:text-white'
                  : 'text-navy-700 after:bg-brand-500 hover:text-brand-600 [&.is-active]:text-brand-600'
              "
            >
              {{ link.label }}
            </a>
          }
        </nav>

        <a
          [href]="'tel:' + business.phone"
          class="hidden shrink-0 items-center gap-2 text-base font-bold transition-colors lg:inline-flex"
          [class]="
            dark() ? 'text-white hover:text-brand-300' : 'text-navy-900 hover:text-brand-600'
          "
        >
          <svg
            class="h-5 w-5"
            [class]="dark() ? 'text-brand-400' : 'text-brand-500'"
            fill="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path [attr.d]="phoneIcon" />
          </svg>
          {{ business.phoneDisplay }}
        </a>

        <div class="flex items-center gap-1 lg:hidden">
          <a
            [href]="'tel:' + business.phone"
            class="grid h-11 w-11 place-items-center rounded-full transition-colors"
            [class]="
              dark() ? 'text-brand-300 hover:bg-white/10' : 'text-brand-600 hover:bg-brand-50'
            "
            [attr.aria-label]="'Call ' + business.phoneDisplay"
          >
            <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path [attr.d]="phoneIcon" />
            </svg>
          </a>

          <button
            type="button"
            class="rounded-lg p-2 transition-colors"
            [class]="dark() ? 'text-white hover:bg-white/10' : 'text-navy-800 hover:bg-navy-50'"
            (click)="toggle()"
            [attr.aria-expanded]="open()"
            aria-label="Toggle navigation"
          >
            @if (open()) {
              <svg
                class="h-6 w-6"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            } @else {
              <svg
                class="h-6 w-6"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5"
                />
              </svg>
            }
          </button>
        </div>
      </div>

      @if (open()) {
        <nav
          class="border-t lg:hidden"
          [class]="dark() ? 'border-white/10' : 'border-navy-100 bg-white'"
          aria-label="Mobile"
        >
          <div class="container-page space-y-1 py-4">
            @for (link of links(); track link.path) {
              <a
                [routerLink]="link.path"
                [routerLinkActiveOptions]="{ exact: link.path === '/' }"
                [routerLinkActive]="
                  dark() ? 'bg-white/10 text-white' : 'bg-brand-50 text-brand-700'
                "
                ariaCurrentWhenActive="page"
                class="block rounded-lg px-3 py-3 text-base font-medium"
                [class]="
                  dark() ? 'text-white/85 hover:bg-white/10' : 'text-navy-800 hover:bg-navy-50'
                "
                (click)="close()"
              >
                {{ link.label }}
              </a>
            }
            <div class="grid gap-2 pt-3 sm:grid-cols-2">
              <a routerLink="/bookings" class="btn-primary w-full" (click)="close()">Book now</a>
              <a
                [href]="'tel:' + business.phone"
                [class]="dark() ? 'btn-outline w-full' : 'btn-ghost w-full'"
              >
                Call {{ business.phoneDisplay }}
              </a>
            </div>
          </div>
        </nav>
      }
    </header>
  `,
})
export class SiteHeaderComponent {
  private readonly gallery = inject(GalleryService);
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private readonly header = viewChild.required<ElementRef<HTMLElement>>('header');

  protected readonly business = environment.business;
  protected readonly phoneIcon = PHONE_ICON;
  protected readonly open = signal(false);

  /** The current page's banner colour, or null when the page opens on a light section. */
  protected readonly bannerColour = signal<string | null>(null);

  /** Whether that banner is still showing behind the header. */
  private readonly overBanner = signal(false);

  protected readonly dark = computed(() => this.bannerColour() !== null && this.overBanner());

  protected readonly links = computed(() => [
    { label: 'Home', path: '/' },
    { label: 'Services', path: '/our-services' },
    { label: 'Packages', path: '/pricing-packages' },
    // Linked only once something is published under Gallery in the admin.
    ...(this.gallery.hasItems() ? [{ label: 'Gallery', path: '/gallery' }] : []),
    { label: 'About', path: '/about-us' },
    { label: 'Contact', path: '/contact-us' },
  ]);

  private observer?: IntersectionObserver;

  constructor() {
    this.gallery.load();

    const destroyRef = inject(DestroyRef);
    destroyRef.onDestroy(() => this.observer?.disconnect());

    // Each page brings its own banner, so look again after every navigation, once
    // the new page has rendered.
    inject(Router)
      .events.pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => afterNextRender(() => this.watchBanner(), { injector: this.injector }));

    afterNextRender(() => this.watchBanner());
  }

  protected toggle(): void {
    this.open.update((value) => !value);
  }

  protected close(): void {
    this.open.set(false);
  }

  private watchBanner(): void {
    this.observer?.disconnect();

    const banner = this.document.querySelector<HTMLElement>('main section');
    const colour = banner ? getComputedStyle(banner).backgroundColor : '';

    if (!banner || !isDarkColour(colour)) {
      this.bannerColour.set(null);
      this.overBanner.set(false);

      return;
    }

    this.bannerColour.set(colour);

    // The banner counts as "behind the header" while any of it is below the
    // header's bottom edge.
    const headerHeight = this.header().nativeElement.offsetHeight;

    this.observer = new IntersectionObserver(
      ([entry]) => this.overBanner.set(entry.isIntersecting),
      { rootMargin: `-${headerHeight}px 0px 0px 0px` },
    );
    this.observer.observe(banner);
  }
}
